import type { ChangeEvent } from "react";
import { useCallback, useEffect, useState } from "react";

import { RadioInput } from "@fronterahealth/frontera-ui-components";

import { FieldLayout } from "@components/BlurBasedFormFields/InlineFormFields/FieldLayout";
import type { InlineFormFieldProps } from "@components/BlurBasedFormFields/InlineFormFields/types";

export const InlineRadioField = <T extends Record<string, unknown>>({
  value,
  fieldKey,
  onSave,
  title,
  className,
  radioOptions,
  transformBeforeSave,
}: Omit<Extract<InlineFormFieldProps<T>, { inputType: "radio" }>, "inputType">) => {
  const stringVal = value === "-" || value == null ? "" : value;
  const [inputValue, setInputValue] = useState(stringVal);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const next = value == null ? "" : value;
    setInputValue((prev) => (prev !== next ? next : prev));
  }, [value]);

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setError(null);
      try {
        const valueToSave = transformBeforeSave ? transformBeforeSave(newValue) : newValue;
        const result = await onSave({ [fieldKey]: valueToSave } as Partial<T>);
        if (!result.success && result.error) setError(result.error);
      } catch (err) {
        console.error("Error saving field:", err);
        setInputValue(stringVal);
        setError("Failed to save changes");
      }
    },
    [fieldKey, onSave, transformBeforeSave, stringVal],
  );

  return (
    <FieldLayout title={title} className={className}>
      <RadioInput
        title=""
        legend={`${title} choice`}
        name={fieldKey as string}
        items={radioOptions}
        selectedId={inputValue}
        onChange={handleChange}
        errorMessage={error ?? undefined}
        inputContainerClassName="min-h-[48px]! rounded-lg"
      />
    </FieldLayout>
  );
};
